import axios from 'axios';
import { isCustomerSessionExpired } from './sessionExpiry';
import { TOKEN_KEY, GUEST_CART_KEY, clearSession } from '../utils/session';

const RAW_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const API_BASE_URL = RAW_API_BASE_URL.replace(/\/+$/, '') === '/api'
  ? ''
  : (RAW_API_BASE_URL || 'http://127.0.0.1:8000');
const CUSTOMER_API_PREFIX = '/api/v1';
const LEGACY_API_PREFIX = '/api/apiv1';

export const API_ENDPOINTS = {
  auth: {
    requestOtp: `${CUSTOMER_API_PREFIX}/auth/otp/request`,
    resendOtp: `${CUSTOMER_API_PREFIX}/auth/otp/resend`,
    verifyOtp: `${CUSTOMER_API_PREFIX}/auth/otp/verify`,
    refreshSession: `${CUSTOMER_API_PREFIX}/auth/session/refresh`,
    logout: `${CUSTOMER_API_PREFIX}/auth/logout`,
  },
  customer: {
    profile: `${CUSTOMER_API_PREFIX}/customer/profile`,
    profileCompletion: `${CUSTOMER_API_PREFIX}/customer/profile/completion`,
    dashboard: `${CUSTOMER_API_PREFIX}/customer/dashboard`,
    sessions: `${CUSTOMER_API_PREFIX}/customer/sessions`,
    logoutOtherSessions: `${CUSTOMER_API_PREFIX}/customer/sessions/logout-others`,
    session: (sessionId) => `${CUSTOMER_API_PREFIX}/customer/sessions/${sessionId}`,
    requestEmailVerification: `${CUSTOMER_API_PREFIX}/customer/email/request-verification`,
    verifyEmail: `${CUSTOMER_API_PREFIX}/customer/email/verify`,
    requestMobileChange: `${CUSTOMER_API_PREFIX}/customer/mobile/request-change`,
    resendMobileChange: `${CUSTOMER_API_PREFIX}/customer/mobile/resend-change`,
    verifyMobileChange: `${CUSTOMER_API_PREFIX}/customer/mobile/verify-change`,
    requestReauthentication: `${CUSTOMER_API_PREFIX}/customer/mobile/reauth/request`,
    resendReauthentication: `${CUSTOMER_API_PREFIX}/customer/mobile/reauth/resend`,
    verifyReauthentication: `${CUSTOMER_API_PREFIX}/customer/mobile/reauth/verify`,
    wishlist: `${CUSTOMER_API_PREFIX}/customer/wishlist`,
    wishlistItem: (productSkuId) => `${CUSTOMER_API_PREFIX}/customer/wishlist/${productSkuId}`,
  },
  addresses: {
    list: `${CUSTOMER_API_PREFIX}/customer/addresses`,
    create: `${CUSTOMER_API_PREFIX}/customer/addresses`,
    detail: (addressId) => `${CUSTOMER_API_PREFIX}/customer/addresses/${addressId}`,
    setDefault: (addressId) => `${CUSTOMER_API_PREFIX}/customer/addresses/${addressId}/default`,
  },
  catalog: {
    menu: `${CUSTOMER_API_PREFIX}/categories/menu`,
  },
  storefront: {
    home: `${CUSTOMER_API_PREFIX}/storefront/home`,
    announcementBar: `${CUSTOMER_API_PREFIX}/storefront/announcement-bar`,
    page: (slug) => `${CUSTOMER_API_PREFIX}/storefront/pages/${slug}`,
    products: `${CUSTOMER_API_PREFIX}/storefront/products`,
    productFilters: `${CUSTOMER_API_PREFIX}/storefront/products/filters`,
    productDetail: (slug) => `${CUSTOMER_API_PREFIX}/storefront/products/${slug}`,
  },
  cart: {
    detail: `${CUSTOMER_API_PREFIX}/cart`,
    pricing: `${CUSTOMER_API_PREFIX}/storefront/cart/pricing`,
    validatePromocode: `${CUSTOMER_API_PREFIX}/storefront/promocodes/validate`,
    items: `${CUSTOMER_API_PREFIX}/cart/items`,
    item: (cartItemId) => `${CUSTOMER_API_PREFIX}/cart/items/${cartItemId}`,
  },
  cartMessages: `${CUSTOMER_API_PREFIX}/cart-checkout/messages`,
  shipping: {
    calculate: `${CUSTOMER_API_PREFIX}/shipping/calculate`,
    states: `${CUSTOMER_API_PREFIX}/shipping/states`,
    statesFallback: `${CUSTOMER_API_PREFIX}/states`,
  },
  legacy: {
    settings: `${LEGACY_API_PREFIX}/settings`,
    login: `${LEGACY_API_PREFIX}/login`,
    register: `${LEGACY_API_PREFIX}/register`,
    sendOtp: `${LEGACY_API_PREFIX}/sendOTP`,
    forgotPasswordOtp: `${LEGACY_API_PREFIX}/forgotPasswordOTP`,
    forgotPasswordAction: `${LEGACY_API_PREFIX}/forgotPasswordAction`,
    setPassword: `${LEGACY_API_PREFIX}/setPassword`,
    changePassword: `${LEGACY_API_PREFIX}/changePassword`,
    menu: `${LEGACY_API_PREFIX}/menu`,
    search: `${LEGACY_API_PREFIX}/search`,
    mainBanner: `${LEGACY_API_PREFIX}/mainBanner`,
    home: `${LEGACY_API_PREFIX}/home`,
    brands: `${LEGACY_API_PREFIX}/brands`,
    products: `${LEGACY_API_PREFIX}/products`,
    productDetails: `${LEGACY_API_PREFIX}/productsDetails`,
    compare: `${LEGACY_API_PREFIX}/compare`,
    cartList: `${LEGACY_API_PREFIX}/cartList`,
    addToCart: `${LEGACY_API_PREFIX}/addtoCart`,
    updateCart: `${LEGACY_API_PREFIX}/updateCart`,
    removeFromCart: `${LEGACY_API_PREFIX}/removefromCart`,
    createOrder: `${LEGACY_API_PREFIX}/createOrder`,
    confirmOrder: `${LEGACY_API_PREFIX}/confirmOrder`,
    orderList: `${LEGACY_API_PREFIX}/orderList`,
  },
};

export const getOtpCooldown = (response) => {
  const body = response?.data;
  const value = body?.data?.resend_after ?? body?.resend_after ?? response?.headers?.['retry-after'];
  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(0, Math.ceil(seconds));
  const retryDate = Date.parse(value);
  return Number.isFinite(retryDate) ? Math.max(0, Math.ceil((retryDate - Date.now()) / 1000)) : 0;
};

export const isOtpRequestEnded = (error) => [
  'OTP_EXPIRED', 'OTP_INVALIDATED', 'OTP_ATTEMPTS_EXCEEDED', 'OTP_REQUEST_NOT_FOUND',
].includes(error?.response?.data?.code);

export const getOtpErrorMessage = (error) => {
  const body = error?.response?.data;
  if (error?.response?.status === 429) {
    const cooldown = getOtpCooldown(error.response);
    const reason = body?.code === 'OTP_ATTEMPTS_EXCEEDED' ? 'Too many incorrect attempts. ' : '';
    return reason + (cooldown > 0
      ? `Please wait ${cooldown} seconds before requesting another OTP.`
      : 'Please wait before requesting another OTP.');
  }
  const messages = {
    OTP_EXPIRED: 'This code has expired. Please request a new OTP.',
    OTP_INVALIDATED: 'This code is no longer valid. Please request a new OTP.',
    OTP_ATTEMPTS_EXCEEDED: 'Too many incorrect attempts. Please request a new OTP.',
    OTP_REQUEST_NOT_FOUND: 'This OTP request is no longer available. Please request a new OTP.',
    OTP_INVALID: 'Incorrect OTP. Please try again.',
  };
  return messages[body?.code] || body?.message || (error?.response?.status === 503
    ? 'SMS service is temporarily unavailable. Please try again later.'
    : error?.response?.status === 429 ? 'Please wait before requesting another OTP.'
      : 'Unable to complete OTP verification. Please try again.');
};

export const getBrowserCountryCode = () => {
  const locale = navigator.languages && navigator.languages.length > 0
    ? navigator.languages[0]
    : navigator.language;
  const country = locale && locale.includes('-') ? locale.split('-').pop() : '';

  return (country || 'IN').toUpperCase();
};

export const getGuestCartId = () => {
  let guestCartId = localStorage.getItem(GUEST_CART_KEY);

  if (!guestCartId) {
    guestCartId = (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
        const value = Math.floor(Math.random() * 16);
        const next = char === 'x' ? value : ((value & 0x3) | 0x8);
        return next.toString(16);
      });
    localStorage.setItem(GUEST_CART_KEY, guestCartId);
  }

  return guestCartId;
};

export const setGuestCartId = (guestCartId) => {
  if (guestCartId) {
    localStorage.setItem(GUEST_CART_KEY, guestCartId);
  }
};

export const clearGuestCartId = () => {
  localStorage.removeItem(GUEST_CART_KEY);
};

export const normalizeCategoryMenu = (categories = [], depth = 0) => {
  if (!Array.isArray(categories)) {
    return [];
  }

  return categories.map((category) => {
    const children = category.categories || category.sub_categories || category.sub_category_list || category.final_category_list || [];
    const normalizedChildren = depth < 1 ? normalizeCategoryMenu(children, depth + 1) : [];

    return {
      ...category,
      main_category_id: category.main_category_id || category.id,
      main_category_name: category.main_category_name || category.name,
      sub_category_id: category.sub_category_id || category.id,
      sub_category_name: category.sub_category_name || category.name,
      final_category_id: category.final_category_id || category.id,
      final_category_name: category.final_category_name || category.name,
      sub_category_list: normalizedChildren,
      final_category_list: normalizedChildren,
    };
  });
};

export const getVisibleCategoryMenu = (categories = []) => {
  const menu = Array.isArray(categories) ? categories : [];
  const visibleMenu = menu.length === 1 && Array.isArray(menu[0].categories)
    ? menu[0].categories
    : menu;

  return normalizeCategoryMenu(visibleMenu);
};

const api = axios.create({
  baseURL: API_BASE_URL,
  responseType: 'json',
  timeout: 30000,
  headers: {
    Accept: 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);

  if (token && !config.publicRequest) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

api.interceptors.response.use(response => response, async error => {
  const token = localStorage.getItem(TOKEN_KEY);
  const requestConfig = error?.config;

  if (isCustomerSessionExpired(error, token) && !requestConfig?._sessionRetried) {
    try {
      await api.post(API_ENDPOINTS.auth.refreshSession, {}, {
        skipSessionRefresh: true,
      });
      return api.request({
        ...requestConfig,
        _sessionRetried: true,
      });
    } catch {
      // Continue to the normal session-expired handling below.
    }
  }

  if (isCustomerSessionExpired(error, token)) {
    clearSession();
    const returnTo = window.location.pathname + window.location.search + window.location.hash;
    window.location.assign(`/signin?reason=expired&returnTo=${encodeURIComponent(returnTo)}`);
  }
  return Promise.reject(error);
});

export const customerApi = {
  requestOtp: (mobile) => api.post(API_ENDPOINTS.auth.requestOtp, { mobile }),
  resendOtp: (requestId) => api.post(API_ENDPOINTS.auth.resendOtp, { request_id: requestId }),
  verifyOtp: (payload) => api.post(API_ENDPOINTS.auth.verifyOtp, payload),
  refreshSession: () => api.post(API_ENDPOINTS.auth.refreshSession, {}, { skipSessionRefresh: true }),
  logout: () => api.post(API_ENDPOINTS.auth.logout),
  getProfile: () => api.get(API_ENDPOINTS.customer.profile),
  updateProfile: (payload) => api.put(API_ENDPOINTS.customer.profile, payload),
  getProfileCompletion: () => api.get(API_ENDPOINTS.customer.profileCompletion),
  requestEmailVerification: (email) => api.post(API_ENDPOINTS.customer.requestEmailVerification, { email }),
  verifyEmail: (payload) => api.post(API_ENDPOINTS.customer.verifyEmail, payload),
  requestMobileChange: (mobile) => api.post(API_ENDPOINTS.customer.requestMobileChange, { mobile }),
  resendMobileChange: (requestId) => api.post(API_ENDPOINTS.customer.resendMobileChange, { request_id: requestId }),
  verifyMobileChange: (payload) => api.post(API_ENDPOINTS.customer.verifyMobileChange, payload),
  requestReauthentication: () => api.post(API_ENDPOINTS.customer.requestReauthentication),
  resendReauthentication: (requestId) => api.post(API_ENDPOINTS.customer.resendReauthentication, { request_id: requestId }),
  verifyReauthentication: (payload) => api.post(API_ENDPOINTS.customer.verifyReauthentication, payload),
  getDashboard: () => api.get(API_ENDPOINTS.customer.dashboard),
  listSessions: () => api.get(API_ENDPOINTS.customer.sessions),
  logoutOtherSessions: () => api.post(API_ENDPOINTS.customer.logoutOtherSessions),
  revokeSession: (sessionId) => api.delete(API_ENDPOINTS.customer.session(sessionId)),
};

let wishlistRequest = null;
let wishlistCache = null;
let wishlistCacheToken = null;

const clearWishlistCache = () => {
  wishlistRequest = null;
  wishlistCache = null;
  wishlistCacheToken = null;
};

const notifyWishlistUpdated = (response) => {
  window.dispatchEvent(new Event('wishlist-updated'));
  return response;
};

export const wishlistApi = {
  list: (options = {}) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (wishlistCacheToken !== token) {
      wishlistCache = null;
      wishlistRequest = null;
      wishlistCacheToken = token;
    }
    if (!options.force && wishlistCache) return Promise.resolve(wishlistCache);
    if (!options.force && wishlistRequest) return wishlistRequest;
    wishlistRequest = api.get(API_ENDPOINTS.customer.wishlist, {
      suppressSessionRedirect: Boolean(options.optional),
    })
      .then((response) => {
        wishlistCache = response;
        wishlistCacheToken = token;
        return response;
      })
      .finally(() => { wishlistRequest = null; });
    return wishlistRequest;
  },
  add: (productSkuId) => api.post(API_ENDPOINTS.customer.wishlist, { product_sku_id: productSkuId }).finally(clearWishlistCache).then(notifyWishlistUpdated),
  remove: (productSkuId) => api.delete(API_ENDPOINTS.customer.wishlistItem(productSkuId)).finally(clearWishlistCache).then(notifyWishlistUpdated),
};

let addressListToken = null;
let addressListRequest = null;
let addressListCache = null;
let addressListCacheTime = 0;
const ADDRESS_LIST_CACHE_MS = 1500;

const clearAddressListCache = () => {
  addressListRequest = null;
  addressListCache = null;
  addressListCacheTime = 0;
};

export const addressApi = {
  list: () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (addressListToken !== token) {
      clearAddressListCache();
      addressListToken = token;
    }
    const now = Date.now();

    if (addressListCache && now - addressListCacheTime < ADDRESS_LIST_CACHE_MS) {
      return Promise.resolve(addressListCache);
    }

    if (addressListRequest) {
      return addressListRequest;
    }

    addressListRequest = api.get(API_ENDPOINTS.addresses.list)
      .then((response) => {
        if (addressListToken !== token) return response;
        addressListCache = response;
        addressListCacheTime = Date.now();
        return response;
      })
      .finally(() => {
        addressListRequest = null;
      });

    return addressListRequest;
  },
  create: (payload) => api.post(API_ENDPOINTS.addresses.create, payload).finally(clearAddressListCache),
  update: (addressId, payload) => api.put(API_ENDPOINTS.addresses.detail(addressId), payload).finally(clearAddressListCache),
  delete: (addressId) => api.delete(API_ENDPOINTS.addresses.detail(addressId)).finally(clearAddressListCache),
  setDefault: (addressId) => api.post(API_ENDPOINTS.addresses.setDefault(addressId)).finally(clearAddressListCache),
};

let cartDetailRequest = null;
let cartDetailCache = null;
let cartDetailCacheKey = '';
let cartDetailCacheTime = 0;
const CART_DETAIL_CACHE_MS = 1500;

const buildCartDetailParams = (params = {}) => ({
  guest_cart_id: getGuestCartId(),
  ...params,
});

const buildCartDetailKey = (params = {}) => buildRequestKey({ ...params, session: localStorage.getItem(TOKEN_KEY) || '' });

const clearCartDetailCache = () => {
  cartDetailRequest = null;
  cartDetailCache = null;
  cartDetailCacheKey = '';
  cartDetailCacheTime = 0;
};

export const cartApi = {
  detail: (params = {}, options = {}) => {
    const requestParams = buildCartDetailParams(params);
    const requestKey = buildCartDetailKey(requestParams);
    const now = Date.now();

    if (!options.force && cartDetailRequest && cartDetailCacheKey === requestKey) {
      return cartDetailRequest;
    }

    if (!options.force && cartDetailCache && cartDetailCacheKey === requestKey && now - cartDetailCacheTime < CART_DETAIL_CACHE_MS) {
      return Promise.resolve(cartDetailCache);
    }

    cartDetailCacheKey = requestKey;
    cartDetailRequest = api.get(API_ENDPOINTS.cart.detail, {
      params: requestParams,
    }).then((response) => {
      if (cartDetailCacheKey !== requestKey) return response;
      cartDetailCache = response;
      cartDetailCacheTime = Date.now();
      return response;
    }).finally(() => {
      if (cartDetailCacheKey === requestKey) cartDetailRequest = null;
    });

    return cartDetailRequest;
  },
  addItem: (payload) => {
    clearCartDetailCache();
    return api.post(API_ENDPOINTS.cart.items, {
      guest_cart_id: getGuestCartId(),
      ...payload,
    }).finally(clearCartDetailCache);
  },
  updateItem: (cartItemId, payload) => {
    clearCartDetailCache();
    return api.patch(API_ENDPOINTS.cart.item(cartItemId), {
      guest_cart_id: getGuestCartId(),
      ...payload,
    }).finally(clearCartDetailCache);
  },
  removeItem: (cartItemId, params = {}) => {
    clearCartDetailCache();
    return api.delete(API_ENDPOINTS.cart.item(cartItemId), {
      data: {
        guest_cart_id: getGuestCartId(),
        ...params,
      },
    }).finally(clearCartDetailCache);
  },
  pricing: (payload) => api.post(API_ENDPOINTS.cart.pricing, payload),
  validatePromocode: (payload) => api.post(API_ENDPOINTS.cart.validatePromocode, payload),
};

// Public optional content must never trigger session expiry redirects.
export const cartMessageApi = {
  evaluate: (payload, signal) => api.post(API_ENDPOINTS.cartMessages, payload, {
    signal,
    publicRequest: true,
    baseURL: API_BASE_URL.replace(/\/+$/, '').replace(/\/api$/, ''),
  }),
};

export const shippingApi = {
  calculate: (payload) => {
    const requestKey = buildRequestKey(payload);
    const now = Date.now();

    if (
      shippingCalculateCache[requestKey]
      && now - shippingCalculateCache[requestKey].time < SHIPPING_CALCULATE_CACHE_MS
    ) {
      return Promise.resolve(shippingCalculateCache[requestKey].response);
    }

    if (shippingCalculateRequests[requestKey]) {
      return shippingCalculateRequests[requestKey];
    }

    shippingCalculateRequests[requestKey] = api.post(API_ENDPOINTS.shipping.calculate, payload)
      .then((response) => {
        shippingCalculateCache[requestKey] = {
          response,
          time: Date.now(),
        };
        return response;
      })
      .finally(() => {
        delete shippingCalculateRequests[requestKey];
      });

    return shippingCalculateRequests[requestKey];
  },
  states: async () => {
    const now = Date.now();

    if (shippingStatesCache && now - shippingStatesCacheTime < SHIPPING_STATES_CACHE_MS) {
      return shippingStatesCache;
    }

    if (shippingStatesRequest) {
      return shippingStatesRequest;
    }

    shippingStatesRequest = (async () => {
      let response;

      try {
        response = await api.get(API_ENDPOINTS.shipping.states);
      } catch (error) {
        if (error.response && error.response.status === 404) {
          response = await api.get(API_ENDPOINTS.shipping.statesFallback);
        } else {
          throw error;
        }
      }

      shippingStatesCache = response;
      shippingStatesCacheTime = Date.now();
      return response;
    })().finally(() => {
      shippingStatesRequest = null;
    });

    return shippingStatesRequest;
  },
};

let shippingStatesRequest = null;
let shippingStatesCache = null;
let shippingStatesCacheTime = 0;
const SHIPPING_STATES_CACHE_MS = 60000;
const shippingCalculateRequests = {};
const shippingCalculateCache = {};
const SHIPPING_CALCULATE_CACHE_MS = 1500;

const catalogMenuCache = {};
const catalogMenuRequests = {};
const storefrontHomeCache = {};
const storefrontHomeRequests = {};
const announcementBarCache = {};
const announcementBarRequests = {};
const storefrontPageCache = {};
const storefrontPageRequests = {};
const storefrontProductRequests = {};
const storefrontProductFilterRequests = {};
const storefrontProductDetailRequests = {};

const buildRequestKey = (params = {}) => {
  const query = new URLSearchParams();
  Object.keys(params || {})
    .sort()
    .forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, String(value));
      }
    });

  return query.toString() || 'default';
};

export const catalogApi = {
  getMenu: async (country = getBrowserCountryCode()) => {
    const countryCode = (country || 'IN').toUpperCase();

    const persistCountry = response => {
      if (Number(response.country_id) > 0) localStorage.setItem('country_id', String(response.country_id));
      return response;
    };

    if (catalogMenuCache[countryCode]) {
      return persistCountry(catalogMenuCache[countryCode]);
    }

    if (catalogMenuRequests[countryCode]) {
      return catalogMenuRequests[countryCode];
    }

    catalogMenuRequests[countryCode] = api.get(API_ENDPOINTS.catalog.menu, {
      params: { country: countryCode },
    }).then((response) => {
      const responseData = response.data || {};
      const menu = getVisibleCategoryMenu(responseData.data || responseData.categories || []);
      catalogMenuCache[countryCode] = {
        ...responseData,
        data: menu,
      };
      delete catalogMenuRequests[countryCode];
      return persistCountry(catalogMenuCache[countryCode]);
    }).catch((error) => {
      delete catalogMenuRequests[countryCode];
      throw error;
    });

    return catalogMenuRequests[countryCode];
  },
};

export const storefrontApi = {
  getAnnouncementBar: async (country = getBrowserCountryCode()) => {
    const countryCode = (country || 'IN').toUpperCase();
    const countryId = localStorage.getItem('country_id');

    if (announcementBarCache[countryId || countryCode]) {
      return announcementBarCache[countryId || countryCode];
    }

    if (announcementBarRequests[countryId || countryCode]) {
      return announcementBarRequests[countryId || countryCode];
    }

    announcementBarRequests[countryId || countryCode] = api.get(API_ENDPOINTS.storefront.announcementBar, {
      params: { country: countryCode },
      headers: countryId ? { 'X-Country-ID': countryId } : undefined,
      publicRequest: true,
    }).then((response) => {
      const resolvedCountryId = response.data?.data?.country_id;
      if (Number.isInteger(Number(resolvedCountryId)) && Number(resolvedCountryId) > 0) {
        localStorage.setItem('country_id', String(resolvedCountryId));
      }
      announcementBarCache[countryId || countryCode] = response;
      delete announcementBarRequests[countryId || countryCode];
      return response;
    }).catch((error) => {
      delete announcementBarRequests[countryId || countryCode];
      throw error;
    });

    return announcementBarRequests[countryId || countryCode];
  },
  getHome: async (country = getBrowserCountryCode()) => {
    const countryCode = (country || 'IN').toUpperCase();
    const cacheKey = countryCode;

    const persistResolvedCountryId = (response) => {
      const resolvedCountryId = response.data?.data?.version?.country_id;
      if (Number.isInteger(Number(resolvedCountryId)) && Number(resolvedCountryId) > 0) {
        localStorage.setItem('country_id', String(resolvedCountryId));
      }
      return response;
    };

    if (storefrontHomeCache[cacheKey]) {
      return persistResolvedCountryId(storefrontHomeCache[cacheKey]);
    }

    if (storefrontHomeRequests[cacheKey]) {
      return storefrontHomeRequests[cacheKey];
    }

    storefrontHomeRequests[cacheKey] = api.get(API_ENDPOINTS.storefront.home, {
      params: { country: countryCode },
      ...(typeof window !== 'undefined' && ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname) ? {timeout: 120000} : {}),
    }).then((response) => {
      persistResolvedCountryId(response);
      storefrontHomeCache[cacheKey] = response;
      delete storefrontHomeRequests[cacheKey];
      return response;
    }).catch((error) => {
      delete storefrontHomeRequests[cacheKey];
      throw error;
    });

    return storefrontHomeRequests[cacheKey];
  },
  getPage: (slug, country = getBrowserCountryCode()) => {
    const countryCode = (country || 'IN').toUpperCase();
    const cacheKey = `${slug}:${countryCode}`;

    if (storefrontPageCache[cacheKey]) {
      return Promise.resolve(storefrontPageCache[cacheKey]);
    }

    if (storefrontPageRequests[cacheKey]) {
      return storefrontPageRequests[cacheKey];
    }

    storefrontPageRequests[cacheKey] = api.get(API_ENDPOINTS.storefront.page(slug), {
      params: { country: countryCode },
    }).then((response) => {
      storefrontPageCache[cacheKey] = response;
      delete storefrontPageRequests[cacheKey];
      return response;
    }).catch((error) => {
      delete storefrontPageRequests[cacheKey];
      throw error;
    });

    return storefrontPageRequests[cacheKey];
  },
  getProducts: (params) => {
    const cacheKey = buildRequestKey(params);

    if (storefrontProductRequests[cacheKey]) {
      return storefrontProductRequests[cacheKey];
    }

    storefrontProductRequests[cacheKey] = api.get(API_ENDPOINTS.storefront.products, { params })
      .finally(() => {
        delete storefrontProductRequests[cacheKey];
      });

    return storefrontProductRequests[cacheKey];
  },
  getProductFilters: (params) => {
    const cacheKey = buildRequestKey(params);

    if (storefrontProductFilterRequests[cacheKey]) {
      return storefrontProductFilterRequests[cacheKey];
    }

    storefrontProductFilterRequests[cacheKey] = api.get(API_ENDPOINTS.storefront.productFilters, { params })
      .finally(() => {
        delete storefrontProductFilterRequests[cacheKey];
      });

    return storefrontProductFilterRequests[cacheKey];
  },
  getProductDetail: (slug, countryId) => {
    const params = countryId ? { country_id: countryId } : {};
    const cacheKey = `${slug}:${buildRequestKey(params)}`;

    if (storefrontProductDetailRequests[cacheKey]) {
      return storefrontProductDetailRequests[cacheKey];
    }

    storefrontProductDetailRequests[cacheKey] = api.get(API_ENDPOINTS.storefront.productDetail(slug), { params })
      .finally(() => {
        delete storefrontProductDetailRequests[cacheKey];
      });

    return storefrontProductDetailRequests[cacheKey];
  },
};

export default api;
